import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

export type Theme = 'light' | 'dark' | 'system';
export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
}

export interface UiState {
  theme: Theme;
  sidebarCollapsed: boolean;
  mobileNavOpen: boolean;
  toasts: Toast[];
}

const THEME_KEY = 'p2h.theme';
const SIDEBAR_KEY = 'p2h.sidebar';

function readStored<T extends string>(key: string, fallback: T): T {
  try {
    return (localStorage.getItem(key) as T | null) ?? fallback;
  } catch {
    return fallback;
  }
}

function applyTheme(theme: Theme): void {
  const dark = theme === 'dark' || (theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* private browsing */
  }
}

const initialState: UiState = {
  theme: readStored<Theme>(THEME_KEY, 'system'),
  sidebarCollapsed: readStored<'true' | 'false'>(SIDEBAR_KEY, 'false') === 'true',
  mobileNavOpen: false,
  toasts: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<Theme>) {
      state.theme = action.payload;
      applyTheme(action.payload);
    },
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
      try {
        localStorage.setItem(SIDEBAR_KEY, String(state.sidebarCollapsed));
      } catch {
        /* private browsing */
      }
    },
    setMobileNav(state, action: PayloadAction<boolean>) {
      state.mobileNavOpen = action.payload;
    },
    pushToast: {
      reducer(state, action: PayloadAction<Toast>) {
        // Keep the stack short; older messages are no longer relevant.
        state.toasts = [...state.toasts.slice(-3), action.payload];
      },
      prepare(input: { title: string; description?: string; tone?: ToastTone }) {
        return { payload: { id: nanoid(), tone: input.tone ?? 'info', ...input } as Toast };
      },
    },
    dismissToast(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },
  },
});

export const { setTheme, toggleSidebar, setMobileNav, pushToast, dismissToast } = uiSlice.actions;
export default uiSlice.reducer;

/** Applied once at boot so the class survives a "system" preference change. */
export function initTheme(theme: Theme): void {
  applyTheme(theme);
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (theme === 'system') applyTheme('system');
  });
}
